import { Loader2 } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PublishTemplateDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The draft about to become current. */
    label: string;
    version: number;
    /** The version this one retires, when the key has ever been published. */
    replacingVersion: number | null;
    isPending: boolean;
    onConfirm: () => void;
}

/**
 * The publish confirmation.
 *
 * IT SPELLS OUT THE ONE THING THAT IS NOT OBVIOUS. Publishing changes what the NEXT document
 * says. `agreement_create` pins the published version onto `agreement_documents.template_id`
 * at generation time and that column never moves, so a document already out for signature —
 * and every document already signed — keeps the wording its parties read. An admin who
 * expects publishing to correct a document currently on somebody's screen would otherwise
 * discover the truth by watching nothing happen.
 *
 * IT IS ALSO A ONE-WAY DOOR. The immutability trigger seals the row on publish: from then on
 * only its status can move. A wording mistake is fixed by a new version, never by an edit.
 */
export default function PublishTemplateDialog({
    open,
    onOpenChange,
    label,
    version,
    replacingVersion,
    isPending,
    onConfirm,
}: PublishTemplateDialogProps) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        Publish {label} v{version}?
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="space-y-3 text-sm text-muted-foreground">
                            <p>
                                Every document generated from now on uses v{version}
                                {replacingVersion !== null && `, and v${replacingVersion} is retired`}.
                            </p>
                            <p>
                                Documents already generated keep the version they were created with — including
                                any still out for signature. To move one of those onto this wording, void and
                                reissue it from the contract's signing board.
                            </p>
                            <p>
                                Publishing seals this version: its wording can never be edited again, and a
                                further change means another version.
                            </p>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(event) => {
                            // The dialog closes itself on action; the mutation owns when this
                            // one goes away, so a failed publish leaves the confirmation up.
                            event.preventDefault();
                            onConfirm();
                        }}
                        disabled={isPending}
                    >
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Publish v{version}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
